import type { OrbitControlsProps } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import React from 'react'
import { useSpring } from 'react-spring'
import * as THREE from 'three'

interface SpringAnimateProps {
  initTarget?: number[]
  initPosition?: number[]
  orbitRef?: OrbitControlsProps | null
  mass?: number
  tension?: number
  friction?: number
  customTargetInterpolation?: (arg: THREE.Vector3) => THREE.Vector3
}

const SpringAnimate = ({
  initTarget,
  initPosition,
  orbitRef,
  mass = 5,
  tension = 280,
  friction = 120,
  customTargetInterpolation,
}: SpringAnimateProps) => {
  const { camera } = useThree()

  const [canAnimate, setCanAnimate] = React.useState<boolean>(false)
  const [target, setTarget] = React.useState(initTarget)
  const [position, setPosition] = React.useState(initPosition)

  const [springs, api] = useSpring(
    () => ({
      config: {
        mass,
        tension,
        friction,
      },
      from: {
        target: initTarget || [],
        position: initPosition || [],
      },
    }),
    []
  )

  React.useEffect(() => {
    if (!canAnimate) {
      api.start({
        from: {
          target,
          position,
        },
        to: {
          target,
          position,
        },
      })
    }
  }, [canAnimate])

  useFrame(() => {
    if (canAnimate) {
      api.start({
        to: {
          target,
          position,
        },
        onChange: ({ value }) => {
          if (canAnimate) {
            if (orbitRef) {
              const orbitControl = orbitRef as OrbitControlsProps
              if (customTargetInterpolation) {
                orbitControl.target = customTargetInterpolation(
                  new THREE.Vector3(...value.position)
                )
              } else {
                orbitControl.target = new THREE.Vector3(...value.target)
              }
            }
            camera.position.fromArray(value.position)
          }
        },
        onRest: (result) => {
          if (result.finished) {
            setCanAnimate(false)
          }
        },
      })
    }
  })

  return {
    canAnimate,
    target,
    position,
    springs,
    setTarget,
    setPosition,
    setCanAnimate,
  }
}

export default SpringAnimate
